import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { useHistory, useLocation } from "react-router-dom";
import { DndProvider, useDrop, useDragLayer } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { agentsData, needsYou } from "Stores/agentsData";
import { openSupportChat } from "Pages/Dashboard/Copilot/agent/agentContract";
import AgentPageSkeleton from "./AgentPageSkeleton";
import AgentsCrumb from "./AgentsCrumb";
import BotGlyph from "./BotGlyph";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import AgentCard, { DND_TYPE, progClass } from "./AgentCard";
import { daysOf, scheduleText as scheduleLabel } from "./DayOfWeekField";
import { stepLabel } from "./stepNames";
import { agentsIntro } from "./AgentTemplates";
import { eventLabel } from "./triggerCatalog";
import { signalsData } from "Stores/signalsData";
import { signalTriggerName } from "../signals/signalMeta";
import { watchCredits } from "Stores/liveCredits";

const ALL = "__all";

export const statusMeta = {
  ACTIVE: { label: "Live", cls: "ag-pill--live" },
  PAUSED: { label: "Paused", cls: "ag-pill--draft" },
  DRAFT: { label: "Draft", cls: "ag-pill--draft" },
  ARCHIVED: { label: "Archived", cls: "ag-pill--draft" }
};

export const agentType = a => {
  const t = a.trigger && a.trigger.type;
  if (t === "schedule.recurring") return "Scheduled";
  if (t === "signal.saved") return "Signal";
  if (t && t.startsWith("event.")) return "Event";
  return "Custom";
};

const TEMPLATE_LABEL = {
  daily_icp_prospector: "Daily ICP prospector", event_agent: "Event agent", list_prospector: "List prospector",
  company_prospector: "Companies to people", lookalike_prospector: "Lookalike companies",
  linkedin_import: "LinkedIn importer", reply_router: "Reply router", account_prospector: "Target accounts (ABM)",
  activity_report: "Daily activity report"
};
export const templateLabel = a => TEMPLATE_LABEL[a.templateKey] || `${agentType(a)} agent`;

export const scheduleText = a => {
  const t = a.trigger;
  if (!t) return "Runs when you start it";
  if (t.type === "schedule.recurring") {
    const cfg = t.config || {};
    return `Runs ${scheduleLabel(cfg.frequency, cfg.time, daysOf(cfg), (a.config && a.config.timezone) || "UTC")}`;
  }
  if (t.type === "signal.saved") {
    const name = signalTriggerName(t.config, signalsData.saved, signalsData.catalog);
    return name ? `Starts from your Signals · ${name}` : "Starts from your Signals";
  }
  if (t.type && t.type.startsWith("event.")) return `Starts in Salesgear · ${eventLabel(t.type.slice(6))}`;
  return "Runs when you start it";
};

// One consistent, subtle bot glyph for every agent.

// Small drag chip that follows the cursor (keeps the folder column visible).
const AgentDragLayer = () => {
  const { isDragging, item, offset } = useDragLayer(m => ({
    isDragging: m.isDragging(),
    item: m.getItem(),
    offset: m.getClientOffset()
  }));
  if (!isDragging || !offset || !item) return null;
  return (
    <div className="ag-draglayer">
      <div className="ag-dragchip" style={{ transform: `translate(${offset.x + 12}px, ${offset.y + 12}px)` }}>
        <span className="ag-dragchip__icon"><BotGlyph size={15} /></span>
        <span className="ag-dragchip__name">{item.name}</span>
      </div>
    </div>
  );
};

const FolderRow = ({ id, label, icon, active, onSelect, onDropAgent, canEdit, onRename, onDelete }) => {
  const [{ isOver }, dropRef] = useDrop({
    accept: DND_TYPE,
    drop: item => onDropAgent(item, id),
    collect: m => ({ isOver: m.isOver() && m.canDrop() })
  });
  // Inline rename, like the agent name: Enter or leaving the field saves, Escape cancels.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);
  const cancelled = useRef(false);
  const save = () => {
    setEditing(false);
    const n = draft.trim();
    if (!cancelled.current && n && n !== label) onRename(label, n);
  };
  if (editing) {
    return (
      <div className="ag-folder ag-folder--new">
        <input className="ag-input" autoFocus value={draft} maxLength={60} aria-label="Folder name"
          onFocus={e => e.target.select()}
          onChange={e => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={e => {
            if (e.key === "Enter") e.target.blur();
            if (e.key === "Escape") { cancelled.current = true; e.target.blur(); }
          }} />
      </div>
    );
  }
  return (
    <div
      ref={dropRef}
      className={`ag-folder ${active ? "selected" : ""} ${isOver ? "dropOver" : ""}`}
      onClick={() => onSelect(id)}
    >
      <i className="material-icons-outlined">{icon}</i>
      <span className="ag-folder__name">{label}</span>
      {isOver && <span className="material-icons-outlined ag-folder__drop">drag_handle</span>}
      {canEdit && !isOver && (
        <span className="ag-folder__actions">
          <i className="material-icons-outlined" title="Rename" onClick={e => {
            e.stopPropagation(); cancelled.current = false; setDraft(label); setEditing(true);
          }}>edit</i>
          <i className="material-icons-outlined" title="Remove folder" onClick={e => { e.stopPropagation(); onDelete(id); }}>close</i>
        </span>
      )}
    </div>
  );
};

// Filters persist across navigation so returning to the page keeps the user's view.
const LS = { q: "agents.filter.q", status: "agents.filter.view", folder: "agents.filter.folder" };
const readLS = (k, d) => { try { const v = localStorage.getItem(k); return v === null ? d : v; } catch (e) { return d; } };

// The filter chips. "Needs you" = waiting on an approval, failing, or a draft one step from running.
const VIEWS = [
  { key: "ALL", label: "All" },
  { key: "RUNNING", label: "Running" },
  { key: "NEEDS", label: "Needs you" },
  { key: "ACTIVE", label: "Live" },
  { key: "PAUSED", label: "Paused" },
  { key: "DRAFT", label: "Drafts" },
  { key: "ARCHIVED", label: "Archived" }
];
const inView = (c, view) => {
  const a = c.agent || {};
  if (view === "ALL") return a.status !== "ARCHIVED";
  if (view === "RUNNING") return c.state === "RUNNING";
  if (view === "NEEDS") return needsYou(c);
  return a.status === view;
};

// A remembered filter that is empty on arrival (e.g. "Running" after the runs finished) falls back to All.
export const restoredView = (view, cards) =>
  view !== "ALL" && cards.length > 0 && !cards.some(c => inView(c, view)) ? "ALL" : view;

// Refresh while something is running so cards move on their own; slower when nothing is.
const POLL_RUNNING_MS = 5000;

// The content is a React node (marked .ag-confirm) so agents.scss can centre the shared popup, and an agent's name
// renders as text rather than HTML.
const confirmThen = (content, actionBtnText, callback) => {
  confirmationPopupState.setPopupValues({
    content: <div className="ag-confirm">{content}</div>, dynamicDom: true,
    actionBtnText, cancelBtnText: "Cancel", needCancelBtn: true, callback
  });
  confirmationPopupState.setShowPopup(true);
};

const AgentsList = observer(() => {
  const history = useHistory();
  const location = useLocation();
  // Check the team's agent limit before opening the new-agent page.
  const hire = async () => { if (await agentsData.canHireAgent()) history.push("/agents/new"); };
  const [q, setQ] = useState(() => readLS(LS.q, ""));
  const [view, setView] = useState(() => readLS(LS.status, "ALL"));
  const [activeFolder, setActiveFolder] = useState(() => readLS(LS.folder, ALL));
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [stepDefs, setStepDefs] = useState(agentsData.stepTypesCache || []);

  useEffect(() => {
    try {
      localStorage.setItem(LS.q, q);
      localStorage.setItem(LS.status, view);
      localStorage.setItem(LS.folder, activeFolder);
    } catch (e) { /* ignore storage failures (private mode, etc.) */ }
  }, [q, view, activeFolder]);

  useEffect(() => {
    (async () => { await agentsData.fetchOverview(); setLoaded(true); })();
    agentsData.fetchUsage();
    agentsData.fetchApprovalCount();
    agentsData.fetchFolders();
    agentsData.fetchStepTypes().then(setStepDefs);
  }, []);

  const overview = agentsData.overview;
  const cards = (overview && overview.agents) || [];
  useEffect(() => { if (loaded) setView(v => restoredView(v, cards)); }, [loaded]); // eslint-disable-line react-hooks/exhaustive-deps
  const summary = (overview && overview.summary) || {};
  const anyRunning = cards.some(c => c.state === "RUNNING");
  // Signal agents' cards name their signal; fetch the (small, cached) signal lists only when one is listed.
  const hasSignalAgent = cards.some(c => agentType(c.agent || {}) === "Signal");
  useEffect(() => {
    if (!hasSignalAgent) return;
    if (!signalsData.catalog.length) signalsData.fetchCatalog();
    if (!signalsData.saved.length) signalsData.fetchSaved();
  }, [hasSignalAgent]);

  useEffect(() => {
    if (!anyRunning) return undefined;
    const t = setInterval(() => {
      if (document.hidden) return;
      agentsData.fetchOverview();
      // The live step chain names steps from the step catalog: if the first load missed, try again.
      if (!agentsData.stepTypesCache) agentsData.fetchStepTypes().then(setStepDefs);
    }, POLL_RUNNING_MS);
    const stopCredits = watchCredits(); // running agents spend: keep the header's credits current
    return () => { clearInterval(t); stopCredits(); };
  }, [anyRunning]);

  // First-run: only AFTER the fetch resolves, if there are truly no agents, go straight to hiring one.
  useEffect(() => {
    if (loaded && overview && cards.length === 0) history.replace("/agents/new");
  }, [loaded, overview, cards.length, history]);

  const folderNames = agentsData.folders || [];
  const stepName = (type, flow, nodeId) => stepLabel(type, stepDefs, flow, nodeId);

  const query = q.trim().toLowerCase();
  const inFolder = c => activeFolder === ALL || ((c.agent && c.agent.folder) || "").trim() === activeFolder;
  const matches = c => !query || String((c.agent && c.agent.name) || "").toLowerCase().includes(query);
  const scoped = cards.filter(c => inFolder(c) && matches(c));
  const visible = scoped.filter(c => inView(c, view));
  const countOf = key => scoped.filter(c => inView(c, key)).length;

  const onDropAgent = async (item, target) => {
    const dest = target === ALL ? "" : target;
    if ((item.folder || "") === dest) return;
    await agentsData.setFolder(item.id, dest);
    agentsData.fetchOverview();
  };
  const addFolder = async () => {
    const n = newName.trim();
    setNewName(""); setCreating(false);
    if (n && !folderNames.includes(n)) { await agentsData.createFolder(n); setActiveFolder(n); }
  };
  const removeFolder = async name => {
    await agentsData.deleteFolder(name);
    if (activeFolder === name) setActiveFolder(ALL);
  };

  const filtersActive = view !== "ALL" || !!query || activeFolder !== ALL;
  const clearFilters = () => { setQ(""); setView("ALL"); setActiveFolder(ALL); };
  // The "Agents" crumb always lands on the full list, even from a folder or filter.
  const resetKey = location.state && location.state.resetAgents;
  useEffect(() => { if (resetKey) clearFilters(); }, [resetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStatus = async (a, status) => { await agentsData.setStatus(a.id, status); agentsData.fetchOverview(); };
  const onAction = (kind, card, fix) => {
    const a = card.agent || {};
    if (kind === "fix" && fix) history.push(fix.to);
    else if (kind === "watch" && card.activeRun) history.push(`/agents/${a.id}/runs/${card.activeRun.id}`);
    else if (kind === "review") history.push(`/agents/approvals?agent=${a.id}`);
    else if (kind === "rename") history.push(`/agents/${a.id}?rename=1`);
    else if (kind === "resume") setStatus(a, "ACTIVE");
    else if (kind === "pause") setStatus(a, "PAUSED");
    else if (kind === "archive") setStatus(a, "ARCHIVED");
    else if (kind === "restore") setStatus(a, "DRAFT");
    else if (kind === "delete") {
      confirmThen(`Delete “${a.name}”? It'll be removed from your list. Its run history is kept and it can be restored later.`,
        "Delete", async () => {
          // The store drops the card and the usage count on success; the refetches just reconcile.
          if (!(await agentsData.deleteAgent(a.id))) return;
          agentsData.fetchApprovalCount();
          agentsData.fetchOverview();
        });
    } else history.push(`/agents/${a.id}${a.status === "DRAFT" && card.state !== "COMPLETED" ? "?setup=1" : ""}`);
  };

  if (!overview) {
    if (!loaded || agentsData.overviewLoading) return <AgentPageSkeleton />;
    if (agentsData.overviewDenied) {
      return (
        <div className="ag-page">
          <div className="ag-empty">
            <div className="ag-empty__icon"><BotGlyph size={30} /></div>
            <div className="ag-empty__title">Agents aren’t on for your team yet</div>
            <div className="ag-empty__text">Agents find people, research them and draft outreach for you on a schedule.</div>
            <div className="ag-empty__actions">
              <button className="ag-btn" onClick={openSupportChat}>Ask us to turn them on</button>
            </div>
          </div>
        </div>
      );
    }
    // The overview request failed: say so, don't spin forever or bounce to "hire" (they may have agents).
    return (
      <div className="ag-page">
        <div className="ag-empty">
          <div className="ag-empty__icon"><BotGlyph size={30} /></div>
          <div className="ag-empty__title">Couldn’t load your agents</div>
          <div className="ag-empty__text">Something went wrong on our side. Try again in a moment.</div>
          <div className="ag-empty__actions">
            <button className="ag-btn ag-btn--primary" onClick={() => agentsData.fetchOverview()}>
              <i className="material-icons-outlined">refresh</i>Try again
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (cards.length === 0) return <AgentPageSkeleton />; // redirecting to /agents/new

  const usage = agentsData.usage;
  const attention = cards.filter(c => needsYou(c) && (c.agent || {}).status !== "ARCHIVED");
  const attentionText = c => {
    const a = c.agent || {};
    if (c.state === "WAITING_APPROVAL") return <><b>{a.name}</b> has {c.waitingContacts} contacts waiting for approval</>;
    if (c.state === "FAILING") return <><b>{a.name}</b> failed its last {c.failedInARow} runs</>;
    return <><b>{a.name}</b> needs its ICP approved before it can run</>;
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <AgentDragLayer />
      <div className="ag-shell">
        <div className="ag-shell__head">
          <AgentsCrumb items={[{ label: activeFolder === ALL ? "All agents" : activeFolder }]} />
          <div className="ag-actions">
            {usage && usage.eligible > 0 && (
              <span className="ag-usagebar" title="Agents your team has, of the number it can have">
                <span className="ag-usagebar__row"><span>Agents used</span><b>{usage.used} of {usage.eligible}</b></span>
                <span className="ag-usagebar__track"><span className={`ag-usagebar__fill ${progClass(usage.used, usage.eligible)}`} /></span>
              </span>
            )}
            <button className="ag-btn" onClick={() => history.push("/agents/templates")}><i className="material-icons-outlined">dashboard</i>Templates</button>
            <button className="ag-btn" onClick={() => history.push("/agents/approvals")}><i className="material-icons-outlined">assignment_turned_in</i>
              Approvals
              {agentsData.pendingApprovalCount > 0 && <span className="ag-nav-badge">{agentsData.pendingApprovalCount}</span>}
            </button>
            <button className="ag-btn ag-btn--primary" onClick={hire}><i className="material-icons-outlined">add</i>Hire an agent</button>
          </div>
        </div>

        <div className="ag-shell__body">
          <aside className="ag-folders">
            <FolderRow id={ALL} label="All agents" icon="folder" active={activeFolder === ALL}
              onSelect={setActiveFolder} onDropAgent={onDropAgent} />
            {folderNames.map(f => (
              <FolderRow key={f} id={f} label={f} icon="folder" active={activeFolder === f}
                onSelect={setActiveFolder} onDropAgent={onDropAgent} canEdit
                onRename={async (from, to) => {
                  await agentsData.renameFolder(from, to);
                  if (activeFolder === from) setActiveFolder(to);
                  agentsData.fetchOverview();
                }}
                onDelete={removeFolder} />
            ))}
            {creating ? (
              <div className="ag-folder ag-folder--new">
                <input className="ag-input" autoFocus placeholder="Folder name" value={newName}
                  onChange={e => setNewName(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") addFolder(); if (e.key === "Escape") { setCreating(false); setNewName(""); } }}
                  onBlur={addFolder} />
              </div>
            ) : (
              <button className="ag-folder ag-folder--add" onClick={() => setCreating(true)}>
                <i className="material-icons-outlined">add</i>
                <span>New folder</span>
              </button>
            )}
          </aside>

          <div className="ag-shell__main">
            <div className="ag-listhead">
              <h1 className="ag-listhead__title">Agents</h1>
              <p className="ag-listhead__sub">{agentsIntro()}</p>
            </div>

            {attention.length > 0 && (
              <div className="ag-needs">
                <span className="ag-needs__label">Needs you</span>
                <span className="ag-needs__items">
                  {attention.slice(0, 3).map((c, i) => (
                    <React.Fragment key={c.agent.id}>
                      {i > 0 && <span className="ag-needs__sep">·</span>}
                      <button className="ag-needs__item" onClick={() => onAction(c.state === "WAITING_APPROVAL" ? "review" : "open", c)}>
                        {attentionText(c)}
                      </button>
                    </React.Fragment>
                  ))}
                  {attention.length > 3 && <span className="ag-needs__sep">+{attention.length - 3} more</span>}
                </span>
              </div>
            )}

            <div className="ag-stats">
              <div className="ag-stat"><span>Live now</span><b>{summary.live || 0}</b></div>
              <div className="ag-stat"><span>Needs you</span><b className={attention.length ? "is-warn" : ""}>{attention.length}</b></div>
              <div className="ag-stat"><span>Contacts found this week</span><b>{summary.foundThisWeek || 0}</b></div>
              <div className="ag-stat"><span>Enrolled in sequences this week</span><b>{summary.enrolledThisWeek || 0}</b></div>
            </div>

            <div className="ag-toolbar ag-toolbar--chips">
              <div className="ag-fchips" role="tablist" aria-label="Filter agents">
                {VIEWS.map(v => {
                  const n = countOf(v.key);
                  if (!n && v.key !== "ALL" && v.key !== view) return null;
                  return (
                    <button key={v.key} role="tab" aria-selected={view === v.key}
                      className={`ag-fchip${view === v.key ? " is-on" : ""}`} onClick={() => setView(v.key)}>
                      {v.label}<span className="ag-fchip__n">{n}</span>
                    </button>
                  );
                })}
              </div>
              <input className="ag-input ag-toolbar__search" placeholder="Search agents" value={q} onChange={e => setQ(e.target.value)} />
            </div>

            {visible.length === 0 ? (
              <div className="ag-empty">
                <div className="ag-empty__icon"><BotGlyph size={30} /></div>
                <div className="ag-empty__title">No agents here</div>
                <div className="ag-empty__text">
                  {filtersActive
                    ? "No agents match this view. Try another filter, or hire a new one."
                    : "Hire your first agent to automate prospecting, follow-ups and more."}
                </div>
                <div className="ag-empty__actions">
                  {filtersActive && <button className="ag-btn" onClick={clearFilters}><i className="material-icons-outlined">clear_all</i>Show all agents</button>}
                  <button className="ag-btn ag-btn--primary" onClick={hire}><i className="material-icons-outlined">add</i>Hire an agent</button>
                </div>
              </div>
            ) : (
              <div className="ag-cards">
                {visible.map(c => (
                  <AgentCard key={c.agent.id} card={c} stepName={stepName}
                    onOpen={x => history.push(`/agents/${x.agent.id}`)} onAction={onAction}
                    scheduleLabel={scheduleText} typeLabel={templateLabel} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </DndProvider>
  );
});

export default AgentsList;
